import React, { useCallback, useEffect, useRef } from "react";
import { handlePhasingNewWindowEvents, useWorkpackagePanelOpened } from "./PhasingNewWindowUtils";
import { useSelector } from "react-redux";
import { AppState } from "../../redux/stateTypes";

const VALID_EVENT_TYPES = new Set([
    'PHASING_-HANDSHAKE-BEGIN',
    'PHASING_-OPEN-WORKPACKAGE',
    'PHASING_-OPEN-SAVED-GANNT-CHART',
    'PHASING_-HANDSHAKE-END',
    'PHASING_-OPEN-ISOLATE-TAG-NAMES',
    'PHASING_SAVE-GANNT-OPEN',
    'PHASING_-HIGHLIGHT-TAG-NAMES',
    'PHASING_-SELECT-TAG-NAMES'
]);

// Stable empty array fallback to avoid new references
const EMPTY_ARRAY: any[] = [];

// Named selector helps diagnostics and avoids new refs via fallback
const selectSelectedWorkpackages = (state: any) => state.wp?.workpackage ?? EMPTY_ARRAY;

const PhasingNewWindowWrapper = ({ children }: { children: React.ReactNode }) => {

    const newWindowRef = useRef<Window | null>(null);
    const currentStore = useSelector((state: AppState) => state.viewer);
    const openPanels = currentStore?.openPanels ?? EMPTY_ARRAY;
    const isWpPanelOpen = useWorkpackagePanelOpened(openPanels);
    const selectedWorkpackages = useSelector(selectSelectedWorkpackages);
    const phasingTasks = useSelector((state: AppState) => state.phasing?.tasks ?? EMPTY_ARRAY);
    const phasingPanelOpened = currentStore?.openPanels?.some(
        (panel: any) => panel.PanelId === 'phasing-panel-id'
    );

    // Keep latest values in refs so the event handler isn't stale
    const isWpPanelOpenRef = useRef(isWpPanelOpen);
    const selectedWorkpackagesRef = useRef(selectedWorkpackages);
    const phasingTasksRef = useRef(phasingTasks);
    const phasingPanelOpenedRef = useRef(phasingPanelOpened);

    useEffect(() => { isWpPanelOpenRef.current = isWpPanelOpen; }, [isWpPanelOpen]);
    useEffect(() => { selectedWorkpackagesRef.current = selectedWorkpackages; }, [selectedWorkpackages]);
    useEffect(() => { phasingTasksRef.current = phasingTasks; }, [phasingTasks]);
    useEffect(() => { phasingPanelOpenedRef.current = phasingPanelOpened; }, [phasingPanelOpened]);

    const messageHandler = useCallback((event: MessageEvent) => {
        try {
            const allowedOrigin = new URL(((globalThis as any)?.store?.getState?.()?.config?.clientBaseUrl) || globalThis.location.origin).origin;
            if (event.origin !== allowedOrigin) return;
        } catch {
            // If origin parsing fails, do not block the message; rely on type allow-list
        }
        const data = event.data as unknown;
        if (!data || typeof data !== 'object' || typeof (data as any).type !== 'string') return;
        const type = (data as any).type as string;
        if (VALID_EVENT_TYPES.has(type)) {
            // Capture the child window reference (new window that sent the message)
            if (event.source && typeof (event.source as Window).postMessage === 'function') {
                const win = event.source as Window;
                newWindowRef.current = win;
            }
            if (type === "PHASING_-HANDSHAKE-BEGIN") {
                (event.source as Window | null)?.postMessage({
                    type: 'PHASING_-HANDSHAKE-END',
                    isWpPanelOpen: isWpPanelOpenRef.current,
                    selectedWorkpackages: selectedWorkpackagesRef.current,
                    phasingPanelOpened: phasingPanelOpenedRef.current,
                    tasks: phasingTasksRef.current
                });
            }
            handlePhasingNewWindowEvents(event, event.source as Window);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', messageHandler);
        return () => {
            window.removeEventListener('message', messageHandler);
        }
    }, [messageHandler]);

    // React to WP panel status changes
    useEffect(() => {
        const win = newWindowRef.current;
        if (win && !win.closed) {
            win.postMessage({
                type: 'PHASING-CHANGE-WP-PANEL-STATUS',
                isWpPanelOpen
            });
        }
    }, [isWpPanelOpen]);

    // React to selected WP changes
    useEffect(() => {
        const win = newWindowRef.current;
        if (win && !win.closed) {
            win.postMessage({
                type: 'PHASING-CHANGE-SELECTED-WP-STATUS',
                selectedWorkpackages
            });
        }
    }, [selectedWorkpackages]);

    // React to phasing panel open state changes
    useEffect(() => {
        const win = newWindowRef.current;
        if (win && !win.closed) {
            win.postMessage({
                type: 'PHASING-CHANGE-IS-PHASING-PANEL-OPEN',
                phasingPanelOpened
            });
        }
    }, [phasingPanelOpened]);


    return <>{children}</>
};

export default PhasingNewWindowWrapper;